import { VERTICAL_MAX_TIERS, } from '@cod/config-schema';
import { outOfStockVariantIds } from './stock.js';
/**
 * Quantity-offer maths and selection — **pure, and shared by both renderers**.
 *
 * These functions were `form-renderer/src/offers.ts`'s exports. They moved here rather than being
 * ported, because the tier arithmetic is the one part of the offer block that MUST agree between
 * the legacy bundle and the NubeSDK app while both ship: a tier that prices differently on the two
 * paths creates two different orders for the same click. `form-renderer` now imports them from
 * `@cod/form-tree/pure`, so there is one implementation and one set of tests.
 *
 * Nothing here touches the DOM, the SDK or `FormState` — the tree builders live in `offers.ts`.
 */
/* ── Pricing ───────────────────────────────────────────────────────────────────────────── */
/**
 * Apply a tier's discount to an already-computed pre-discount line total. Never below zero.
 *
 * Split out from `tierPrice` so callers that price a tier from a MIX of per-unit variant prices
 * (each unit a different variant, hence a different price) can discount that variant-summed base.
 */
export function applyTierDiscount(base, tier) {
    if (tier.discount_type === 'percentage') {
        return Math.max(0, base * (1 - tier.discount_value / 100));
    }
    if (tier.discount_type === 'fixed') {
        return Math.max(0, base - tier.discount_value);
    }
    return base;
}
/**
 * The line total for a tier after its discount, priced from a single unit price. Used when the
 * tier's units are all the same variant (or the product has no variants).
 */
export function tierPrice(unitPrice, tier) {
    return applyTierDiscount(unitPrice * tier.quantity, tier);
}
/** Whether the tier actually discounts, so a struck-through comparison price is meaningful. */
export function hasDiscount(tier) {
    return tier.discount_type !== 'none' && tier.discount_value > 0;
}
/** Per-variant unit prices from the live context. Empty in the admin preview, which has none. */
export function variantPrices(ctx) {
    const prices = new Map();
    for (const v of ctx.product.variants ?? []) {
        if (typeof v.price === 'number')
            prices.set(v.id, v.price);
    }
    return prices;
}
/**
 * Pre-discount line total for a tier, given the per-unit variant ids currently chosen.
 *
 * Sums the selected variants' prices when they are all known; otherwise falls back to
 * `unitPrice × quantity`. A tier whose units are DIFFERENT variants must total what those variants
 * actually cost — pricing three mixed picks at the page variant's price is simply wrong.
 */
export function tierBase(tier, ctx, variantIds) {
    const prices = variantPrices(ctx);
    if (variantIds && variantIds.length === tier.quantity && prices.size > 0) {
        return variantIds.reduce((sum, id) => sum + (prices.get(id) ?? ctx.variant.price), 0);
    }
    return ctx.variant.price * tier.quantity;
}
/* ── Which offer applies ───────────────────────────────────────────────────────────────── */
/**
 * The offer to show for a product: the highest-priority (first in array) ENABLED offer whose
 * target products include `productId`, or null. Only one offer applies per product — the admin
 * warns when several target the same one, and here the first wins deterministically.
 */
export function selectOfferForProduct(offers, productId) {
    return offers.find((o) => o.enabled && o.products.some((p) => p.id === productId)) ?? null;
}
/**
 * The product id an offer must be matched against for a render context.
 *
 * In cart mode `product.id` is merely the FIRST cart line, so the page's own product is
 * authoritative; the cart drawer carries no page product and therefore matches nothing.
 */
export function offerProductIdOf(ctx) {
    return ctx.lineItems ? (ctx.pageProductId ?? null) : ctx.product.id;
}
/** The largest quantity any of the offer's tiers sells (0 for an offer with no tiers). */
export function maxOfferQuantity(offer) {
    return offer.tiers.reduce((max, tier) => Math.max(max, tier.quantity), 0);
}
/**
 * Whether an offer still applies given the quantity the buyer chose on the product page.
 *
 * With "disable when qty exceeds" on, an offer is suppressed once the quantity is greater than its
 * largest tier — the buyer asked for more than any tier covers, so the form falls back to a plain
 * "product × N" order rather than a smaller bundle. Always true when the guard is off.
 */
export function offerAppliesForQuantity(offer, quantity) {
    if (!offer.design.disable_when_qty_exceeds)
        return true;
    return quantity <= maxOfferQuantity(offer);
}
/**
 * The tiers an offer actually displays. Every template shows all of them except `vertical`, whose
 * narrow side-by-side cards are capped so the layout stays readable. Both the cards and the order
 * read this, so the buyer can never end up on a tier they cannot see.
 */
export function visibleOfferTiers(offer) {
    return offer.design.template === 'vertical'
        ? offer.tiers.slice(0, VERTICAL_MAX_TIERS)
        : offer.tiers;
}
/**
 * The offer for a render context, or null. Ports `storefrontOffer` (`form-renderer/index.ts:54`).
 *
 * Callers pass the buyer's current quantity so the "disable when qty exceeds" guard is applied
 * here rather than being forgotten at one of the call sites.
 */
export function resolveOffer(config, ctx, quantity = 1) {
    const productId = offerProductIdOf(ctx);
    if (productId == null)
        return null;
    const offer = selectOfferForProduct(config._future.quantity_offers, productId);
    return offer && offerAppliesForQuantity(offer, quantity) ? offer : null;
}
/**
 * The tier a STANDALONE offer widget opens on: the preselected visible one, else the first.
 *
 * 🚨 The `?? shown[0]` fallback belongs to the above-the-button cards and nothing else. Those are a
 * purchase widget in their own right — the buyer presses Buy with a tier active — so one card has
 * to be chosen at all times. A form is not that; see `preselectedTier` below.
 */
export function initialTier(offer) {
    const shown = visibleOfferTiers(offer);
    return shown.find((t) => t.preselect) ?? shown[0];
}
/**
 * The tier the FORM opens on: only one the merchant actually preselected. No fallback.
 *
 * 🚨 The form used `initialTier`, and its `?? shown[0]` quietly forced a tier on every open. Two
 * things followed, both reported from a live store:
 *
 *  - **The cart's quantity was overwritten.** A one-line cart seeds `state.quantity` from the line
 *    (4 units stays 4), and the tier branch then replaced it with `tier.quantity` — so opening the
 *    form on a cart of 4 ordered 1, silently, as if the buyer had clicked the first card.
 *  - **No other quantity could be chosen.** `singleLineStepper` stands down while a tier is
 *    active, because a tier owns the order's quantity. With one always active the stepper never
 *    appeared, so the buyer was limited to the quantities the offer happened to define.
 *
 * "Nothing preselected" is a real merchant choice and has to mean nothing selected: the cards show
 * as an offer the buyer may take, the stepper keeps working, and a cart that already earns a tier's
 * quantity still gets its discount through `cartOfferDiscount` without any card being clicked. The
 * legacy bundle has always behaved this way — its `selectedTier` starts `null` and only an
 * above-the-button tier applies one — so this is parity, not a new rule.
 */
export function preselectedTier(offer) {
    return visibleOfferTiers(offer).find((t) => t.preselect);
}
/** The visible tier with this id, or undefined. */
export function tierById(offer, tierId) {
    return tierId ? visibleOfferTiers(offer).find((t) => t.id === tierId) : undefined;
}
/** Distinct entries preserving first-seen order (small lists, so a linear scan is fine). */
export function distinct(values) {
    const out = [];
    for (const v of values)
        if (!out.includes(v))
            out.push(v);
    return out;
}
/** Keys an (option0, option1) pair for the id lookup; NUL cannot appear in an option value. */
export function pairKey(a, b) {
    return `${a} ${b}`;
}
/**
 * Whether the variants render as two dependent dropdowns (Color, then Size) rather than one
 * combined "Color / Size" field. Exactly two option types split; one, or three-plus, stay
 * combined. Requires every variant to expose both values.
 */
export function shouldSplit(variants, optionNames) {
    return (optionNames.length === 2 &&
        variants.length > 0 &&
        variants.every((v) => v.values != null && v.values.length === 2));
}
/**
 * Where the picker's variants and option names come from.
 *
 * The OFFER's captured product is authoritative — it was recorded in the admin, so the storefront
 * needs no catalogue lookup. The live context is only the fallback the admin preview uses, whose
 * sample product id will not match. Context entries with a blank name are price-only rows and are
 * useless as a label source.
 */
export function offerVariantSource(offer, ctx) {
    const product = offer.products.find((p) => p.id === offerProductIdOf(ctx)) ?? offer.products[0];
    const fromCtx = (ctx.product.variants ?? []).filter((v) => v.name);
    // 🚨 `?.variants?.length`, not `?.variants.length`. `offerProduct.variants` is `.default([])`, so
    // a config that PARSES always has it — but `normalizeFormConfig`'s fallback branch (taken when
    // `safeParse` fails for any reason, anywhere in the document) hands the raw stored object
    // through, and the storefront casts the fetched JSON rather than re-parsing it. An offer product
    // saved before this key existed then reaches here as `undefined` and throws.
    //
    // The throw is what makes it serious: `main.ts`'s render loop swallows per-slot errors so one bad
    // surface cannot take the other down, so the COD button just SILENTLY VANISHES from the product
    // page. Degrading to "no per-unit pickers" is the right failure — the offer still sells.
    const variants = product?.variants?.length ? product.variants : fromCtx;
    const optionNames = product?.variant_options?.length
        ? product.variant_options
        : (ctx.product.variant_options ?? []);
    return {
        variants,
        optionNames,
        showPickers: !offer.design.disable_variant_selectors && variants.length > 1,
    };
}
/** The two dependent levels of a split picker, derived from the variant list. */
export function splitLevels(variants, outOfStock = EMPTY_IDS) {
    const idByPair = new Map();
    for (const v of variants) {
        if (v.values?.length === 2)
            idByPair.set(pairKey(v.values[0], v.values[1]), v.id);
    }
    const idFor = (first, second) => idByPair.get(pairKey(first, second));
    const secondsFor = (first) => distinct(variants.filter((v) => v.values?.[0] === first).map((v) => v.values?.[1] ?? ''));
    const pairSoldOut = (first, second) => {
        const id = idFor(first, second);
        return id != null && outOfStock.has(id);
    };
    return {
        firsts: distinct(variants.map((v) => v.values?.[0] ?? '')),
        secondsFor,
        idFor,
        firstSoldOut: (first) => {
            const seconds = secondsFor(first);
            return seconds.length > 0 && seconds.every((second) => pairSoldOut(first, second));
        },
        pairSoldOut,
    };
}
/** Shared empty set, so the default argument does not allocate on every render. */
const EMPTY_IDS = new Set();
/**
 * The variant a picker should OPEN on: the resolved id when this picker offers it and it can be
 * bought, else the first entry that can be.
 *
 * 🚨 A `select` whose value is a disabled option is the worst of both worlds — the buyer sees a
 * sold-out size preselected and cannot change it back, and a submit takes the order anyway. So
 * greying options out is only half the fix; the opening value has to skip them too.
 *
 * Falls back to `resolved` when nothing in the list is buyable, rather than to nothing: a picker
 * showing an empty selection is worse than one showing a sold-out variant it also disables, and
 * the product surface should not be on screen at all in that state (`isOutOfStock`).
 */
export function pickerDefaultId(variants, resolved, outOfStock = EMPTY_IDS) {
    const offered = variants.some((v) => v.id === resolved);
    if (offered && !outOfStock.has(resolved))
        return resolved;
    return variants.find((v) => !outOfStock.has(v.id))?.id ?? resolved;
}
/**
 * One variant id per unit the tier sells.
 *
 * Unset slots default to the variant the buyer already chose on the product page, so a freshly
 * selected tier prices exactly like the product card until they change a dropdown. Trailing slots
 * are dropped and missing ones filled when the tier's quantity changes, which is what keeps the
 * array's length equal to the tier's quantity — `tierBase` only trusts it when it is.
 *
 * 🚨 This is also what makes the pickers FOLLOW the product page: `chosen` is sparse (see
 * `FormState.tierVariantIds`), and every unset slot re-resolves from `ctx` on each build — which
 * the storefront runs on `product:variant_selected`. So this must be the only way state reaches a
 * price or a picker; passing `state.tierVariantIds` straight to `tierBase` both skips the fallback
 * and hands it `undefined` entries.
 *
 * 🚨 The fallback is ALWAYS the buyer's page variant, on BOTH bundles. A retired design
 * flag (`use_first_variant`) once let an offer whose pickers are disabled build its bundle from
 * the product's FIRST variant instead — units of a colour the buyer never picked. The legacy
 * renderer never implemented it (`form-renderer/src/offers.ts` always seeded `ctx.variant.id`), so
 * the two paths already disagreed; it was removed rather than ported. Do not reintroduce an
 * `offer` argument here.
 */
export function unitVariantIds(tier, ctx, chosen) {
    // 🚨 Never default a unit to a variant that cannot be bought. The product surface only appears
    // when the buyer's page variant is in stock, so this is defence rather than the common path —
    // but a context where it is not (a stale snapshot, a cart-mode context, a variant that sold out
    // between page load and opening the form) would otherwise seed every unit with a sold-out id and
    // price, display and ORDER it. An explicit pick is left alone: the pickers disable sold-out
    // options, so one cannot be chosen, and overriding a buyer's choice here would be worse.
    const soldOut = outOfStockVariantIds(ctx);
    const fallback = soldOut.has(ctx.variant.id)
        ? (ctx.product.variants?.find((v) => !soldOut.has(v.id))?.id ?? ctx.variant.id)
        : ctx.variant.id;
    return Array.from({ length: tier.quantity }, (_unused, i) => chosen?.[i] ?? fallback);
}
/**
 * The order lines a tier sells: one per DISTINCT variant picked (first-seen order, quantities
 * grouped). Ports `tierLineItems` (`form-renderer/src/form.ts:513`).
 *
 * Priced from the live per-variant prices so a mixed pick totals what it actually costs.
 */
export function tierLineItems(tier, ctx, variantIds) {
    const prices = variantPrices(ctx);
    const labels = new Map((offerVariantSourceless(ctx) ?? []).map((v) => [v.id, v.name]));
    const image = ctx.product.image;
    const grouped = new Map();
    for (const id of variantIds)
        grouped.set(id, (grouped.get(id) ?? 0) + 1);
    return [...grouped].map(([variantId, quantity]) => ({
        ...(ctx.product.id != null ? { id: ctx.product.id } : {}),
        variantId,
        name: ctx.product.name,
        quantity,
        price: prices.get(variantId) ?? ctx.variant.price,
        ...(image ? { image } : {}),
        ...(labels.get(variantId) ? { variant: labels.get(variantId) } : {}),
    }));
}
/**
 * Swap the PAGE PRODUCT's cart lines for the tier's lines. Ports `syncOfferLines`
 * (`form-renderer/src/form.ts:547`).
 *
 * 🚨 Without this a cart-mode order with a tier is priced from the cart's own line. The buyer picks
 * "2 for 25% off" and the summary still says "× 1" at the single-unit price, because the tier sells
 * 2 units of a product the cart holds 1 of — so the offer's discount also lands on the wrong base.
 *
 * The tier's lines replace the page product's at ITS position; every other cart item, and anything
 * the buyer removed with the per-item ✕, is left exactly where it was. Clearing the tier restores
 * the original lines for free, because this derives from `ctx.lineItems` on every render rather
 * than mutating a running copy the way the legacy did.
 */
export function withTierLines(lines, ctx, tier, variantIds) {
    const pageProductId = offerProductIdOf(ctx);
    if (!tier || pageProductId == null)
        return lines;
    // 🚨 Inherit the thumbnail from the line being replaced. `tierLineItems` takes its image from
    // `ctx.product.image`, which a CART context does not carry — `cartContextFromState` builds its
    // `product` from the first cart line and has no image to put there. Without this the summary
    // swapped a thumbnailed cart line for a blank grey box the moment a tier was picked.
    const replaced = lines.find((li) => li.id === pageProductId);
    const own = tierLineItems(tier, ctx, variantIds).map((li) => li.image || !replaced?.image ? li : { ...li, image: replaced.image });
    const next = [];
    let placed = false;
    for (const li of lines) {
        if (li.id !== pageProductId) {
            next.push(li);
            continue;
        }
        if (!placed) {
            next.push(...own);
            placed = true;
        }
    }
    // The buyer removed the page product's line before picking a tier — the offer still sells it.
    if (!placed)
        next.push(...own);
    return next;
}
/** Variant labels available from the context alone, for naming a tier's lines. */
function offerVariantSourceless(ctx) {
    return ctx.product.variants?.filter((v) => v.name).map((v) => ({ id: v.id, name: v.name }));
}
/**
 * The discount a set of cart lines earns **from their quantity alone**, with no tier selected.
 *
 * 🚨 This exists because the native cart discounts itself. The Discount API applies a promotion to
 * Tiendanube's cart on every mutation, derived purely from what the cart holds — nobody clicks a
 * tier for it. So a COD form opened from the cart drawer that priced only explicitly-selected
 * tiers showed a total HIGHER than the drawer behind it: the drawer said €27,00 and the form said
 * €30,00 for the same two units.
 *
 * The rule is a deliberate mirror of `backend/domain/discountCommands.ts` (`bestTierFor` +
 * `discountAmount`) — the highest tier the matched quantity reaches, over the matched lines' value
 * — because agreeing with the platform is the entire point. Per offer independently, and an offer
 * with no captured products targets the whole cart, both exactly as the callback does. (Two offers
 * targeting one product would therefore both discount it; the admin warns on that overlap, and
 * diverging here to "first wins" would put the form back out of step with the cart.)
 *
 * `exclude` drops an offer already priced by an active tier, so the two rules never double-count
 * the same lines in "Product + Cart" mode. Resolve it with `offerOwningTier`, not from whichever
 * offer the page matched: an offer rendered ABOVE the buy button is not the form's own offer, and
 * keying on that let its tier discount land twice — once from the tier, once from the quantity.
 */
export function cartOfferDiscount(offers, lines, exclude) {
    let total = 0;
    for (const offer of offers) {
        if (!offer.enabled || (exclude && offer.id === exclude.id))
            continue;
        const ids = new Set(offer.products.map((p) => p.id));
        const matched = ids.size === 0 ? lines : lines.filter((l) => l.id != null && ids.has(l.id));
        if (matched.length === 0)
            continue;
        const quantity = matched.reduce((sum, l) => sum + l.quantity, 0);
        const tier = bestTierForQuantity(offer, quantity);
        if (!tier)
            continue;
        const base = matched.reduce((sum, l) => sum + l.price * l.quantity, 0);
        total += Math.max(0, base - applyTierDiscount(base, tier));
    }
    return total;
}
/**
 * The discount a matched quantity earns: the highest `quantity` threshold actually reached, when
 * that tier carries a discount. Nothing when none is reached — a two-unit cart under a "buy 3"
 * offer pays full price, in the form exactly as in the drawer.
 *
 * 🚨 The reached tier is found FIRST, and only then checked for a discount. Filtering on
 * `hasDiscount` before sorting made a cart fall back to a lower tier: an offer with tiers 1, 2, 3
 * and 4 where only 1 and 2 carry discounts gave a FOUR-unit cart the two-unit discount, because
 * tiers 3 and 4 were filtered away and 2 was then the highest "reachable" one. The buyer earns the
 * tier their quantity actually reaches; if the merchant priced that tier at no discount, that is
 * the answer, not a licence to hunt downward for a cheaper one.
 *
 * 🚨 Mirrors `bestTierFor` in `backend/domain/discountCommands.ts`, which is what discounts the
 * NATIVE cart. The two must move together or the form and the drawer quote different totals for
 * the same basket — the divergence `cartOfferDiscount` above exists to prevent.
 */
export function bestTierForQuantity(offer, quantity) {
    const reached = offer.tiers
        .filter((t) => t.quantity <= quantity)
        .sort((a, b) => b.quantity - a.quantity)[0];
    return reached && hasDiscount(reached) ? reached : null;
}
/**
 * The offer a selected tier belongs to. The tier id is the only handle the form always has: an
 * above-the-button offer hands the modal a tier snapshot rather than the offer itself.
 */
export function offerOwningTier(offers, tierId) {
    if (!tierId)
        return null;
    return offers.find((o) => o.tiers.some((t) => t.id === tierId)) ?? null;
}
/**
 * The offer the tier branch has ALREADY priced, and therefore the only one the quantity rule must
 * skip. `undefined` — skip nothing — whenever no tier is priced.
 *
 * 🚨 This is the fix for a discount that vanished from the totals while the ORDER still carried it.
 * Both bundles resolved the exclusion as `formOffer ?? offerOwningTier(offers, selectedTierId)`,
 * which reads as a fallback but is the wrong way round: `formOffer` is the offer resolved for the
 * PAGE, present whether or not the buyer ever clicked a tier. So a cart form on that product's page
 * excluded the one offer covering its lines, `cartOfferDiscount` returned 0, and the "Descuento"
 * row was not drawn — while the backend, which excludes only on an explicit `offer` in the
 * submission (`resolveCartOfferDiscount(config, lines, input.offer?.offer_id)`), discounted the
 * order anyway. Two units of a 2-for offer quoted full price and were charged less. Reported from a
 * live store; both bundles' comments already described this rule correctly, only the code did not.
 *
 * Keyed on the tier the caller actually priced, not on `selectedTierId`: a tier id can be set while
 * the tier branch prices nothing (the tier belongs to another offer, or the merchant hid it), and
 * excluding then would drop a discount nobody had counted.
 *
 * 🚨 Shared, like everything else in this module: the legacy totals box and the NubeSDK one must
 * agree on what the same basket costs, and the buyer pays the quoted figure in cash at the door.
 */
export function quantityDiscountExclusion(offers, pricedTier, formOffer) {
    if (!pricedTier)
        return undefined;
    return offerOwningTier(offers, pricedTier.id) ?? formOffer ?? undefined;
}
//# sourceMappingURL=offers.logic.js.map